import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, FileText, Download, Printer, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import ReferralCoverPage from './referral/ReferralCoverPage';
import OMHCReferralForm from './referral/OMHCReferralForm';
import PRPReferralForm from './referral/PRPReferralForm';
import SignatureAuthPage from './referral/SignatureAuthPage';
import AttachmentsChecklist from './referral/AttachmentsChecklist';
import { downloadReferralPDF } from './referral/pdfGenerator';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STORAGE_KEY = 'sdora_referral_draft';

const loadDraft = () => {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed && parsed.formData && parsed.savedAt) {
        const age = Date.now() - parsed.savedAt;
        // Expire drafts after 7 days
        if (age < 7 * 24 * 60 * 60 * 1000) return parsed;
      }
    }
  } catch {
    // Storage error - proceed without draft
  }
  return null;
};

const saveDraft = (formData, currentPage) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ formData, currentPage, savedAt: Date.now() }));
  } catch {
    // Storage error - proceed without saving
  }
};

const clearDraft = () => {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
};

const ReferralFormModal = ({ onClose }) => {
  const draft = loadDraft();
  const [currentPage, setCurrentPage] = useState(draft ? draft.currentPage : 0);
  const [submitState, setSubmitState] = useState('idle');
  const [submitMessage, setSubmitMessage] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [draftRestored, setDraftRestored] = useState(!!draft);
  const [formData, setFormData] = useState(draft ? draft.formData : {
    referralDate: new Date().toISOString().split('T')[0],
    referralSource: '',
    referringProvider: '',
    credentials: '',
    npi: '',
    phone: '',
    fax: '',
    email: '',
    contactMethod: 'Phone',
    urgencyLevel: 'Routine',
    clientType: 'Adult',
    referralCategory: '',
    preferredTimeframe: 'Within 14 days',
    referralStatus: 'New Referral',
    omhc: {},
    prp: {},
    signatures: {},
    attachments: []
  });

  const getPages = () => {
    const category = formData.referralCategory;
    const result = [{ title: 'Cover / Intake', component: ReferralCoverPage }];

    if (category === 'OMHC' || category === 'Both') {
      result.push({ title: 'OMHC Referral', component: OMHCReferralForm });
    }
    if (category === 'PRP' || category === 'Both') {
      result.push({ title: 'PRP Referral', component: PRPReferralForm });
    }

    result.push({ title: 'Signatures', component: SignatureAuthPage });
    result.push({ title: 'Attachments', component: AttachmentsChecklist });

    return result;
  };

  const pages = getPages();

  // Auto-save draft on every change
  useEffect(() => {
    if (submitState !== 'success') {
      saveDraft(formData, currentPage);
    }
  }, [formData, currentPage, submitState]);

  // Dismiss draft restored banner after 5s
  useEffect(() => {
    if (draftRestored) {
      const t = setTimeout(() => setDraftRestored(false), 5000);
      return () => clearTimeout(t);
    }
  }, [draftRestored]);

  const handleDataUpdate = (pageData) => {
    setFormData(prev => ({ ...prev, ...pageData }));
    // Clear validation errors when user updates data
    if (Object.keys(validationErrors).length > 0) {
      setValidationErrors({});
    }
  };

  // Validation functions
  const validateCoverPage = () => {
    const errors = {};
    if (!formData.credentials || formData.credentials.trim() === '') {
      errors.credentials = 'Credentials are required';
    }
    if (!formData.npi || formData.npi.trim() === '') {
      errors.npi = 'NPI Number is required';
    }
    if (!formData.email || formData.email.trim() === '') {
      errors.email = 'Email is required';
    }
    if (!formData.referralCategory) {
      errors.coverPage = true;
    }
    // Clinical Supervision validation
    if (!formData.underSupervision) {
      errors.underSupervision = 'Please indicate if the referring provider is under clinical supervision';
    }
    if (formData.underSupervision === 'Yes') {
      if (!formData.supervisorName?.trim()) errors.supervisorName = 'Supervisor name is required';
      if (!formData.supervisorCredentials?.trim()) errors.supervisorCredentials = 'Supervisor credentials are required';
      if (!formData.supervisorNPI?.trim()) errors.supervisorNPI = 'Supervisor NPI is required';
      else if (formData.supervisorNPI.replace(/\D/g, '').length !== 10) errors.supervisorNPI = 'NPI must be exactly 10 digits';
      if (!formData.supervisorPhone?.trim()) errors.supervisorPhone = 'Supervisor phone is required';
      if (!formData.supervisorEmail?.trim()) errors.supervisorEmail = 'Supervisor email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.supervisorEmail)) errors.supervisorEmail = 'Please enter a valid email';
    }
    return errors;
  };

  const validateOMHCPage = () => {
    const errors = {};
    const omhcData = formData.omhc || {};
    // Contact Information mandatory fields
    if (!omhcData.homeAddressLine1 || omhcData.homeAddressLine1.trim() === '') errors.omhcHomeAddress = 'Address Line 1 is required';
    if (!omhcData.homeCity || omhcData.homeCity.trim() === '') errors.omhcHomeCity = 'City is required';
    if (!omhcData.homeState) errors.omhcHomeState = 'State is required';
    if (!omhcData.homeZip || omhcData.homeZip.trim() === '') errors.omhcHomeZip = 'Zip Code is required';
    if (!omhcData.clientPhone || omhcData.clientPhone.trim() === '') errors.omhcPhone = 'Phone is required';
    if (Object.keys(errors).length > 0) errors.omhcRequired = true;
    return errors;
  };

  const validatePRPPage = () => {
    const errors = {};
    const prpData = formData.prp || {};

    // Required PRP fields
    if (!prpData.lastName || prpData.lastName.trim() === '') {
      errors.prpLastName = 'Last Name is required';
      errors.prpRequired = true;
    }
    if (!prpData.firstName || prpData.firstName.trim() === '') {
      errors.prpFirstName = 'First Name is required';
      errors.prpRequired = true;
    }
    if (!prpData.dob) {
      errors.prpDob = 'Date of Birth is required';
      errors.prpRequired = true;
    }
    if (!prpData.currentOMHC) {
      errors.prpCurrentOMHC = 'Please select Yes or No';
      errors.prpRequired = true;
    }

    // Contact Information mandatory fields
    if (!prpData.homeAddressLine1 || prpData.homeAddressLine1.trim() === '') { errors.prpHomeAddress = 'Address Line 1 is required'; errors.prpRequired = true; }
    if (!prpData.homeCity || prpData.homeCity.trim() === '') { errors.prpHomeCity = 'City is required'; errors.prpRequired = true; }
    if (!prpData.homeState) { errors.prpHomeState = 'State is required'; errors.prpRequired = true; }
    if (!prpData.homeZip || prpData.homeZip.trim() === '') { errors.prpHomeZip = 'Zip Code is required'; errors.prpRequired = true; }
    if (!prpData.clientPhone || prpData.clientPhone.trim() === '') { errors.prpPhone = 'Phone is required'; errors.prpRequired = true; }

    // Clinical Information mandatory fields
    if (!prpData.currentDiagnosis) { errors.prpDiagnosis = 'Current Diagnosis is required'; errors.prpRequired = true; }
    if (!prpData.symptomsAndReason || prpData.symptomsAndReason.trim() === '') { errors.prpSymptoms = 'Current Symptoms & Reason is required'; errors.prpRequired = true; }

    // Functional criteria validation (Adult only)
    if (formData.clientType === 'Adult') {
      const criteria = prpData.functionalCriteria || {};
      const selectedKeys = Object.keys(criteria);

      if (selectedKeys.length < 3) {
        errors.functionalCriteria = `Please select at least 3 functional criteria (${selectedKeys.length} selected)`;
      } else {
        selectedKeys.forEach(key => {
          if (!criteria[key].evidence || criteria[key].evidence.trim() === '') {
            errors[`evidence_${key}`] = 'Evidence is required for this criterion';
          }
        });
      }
    }

    return errors;
  };

  const validateSignaturePage = () => {
    const errors = {};
    const sigs = formData.signatures || {};
    const prpData = formData.prp || {};
    const isPRP = formData.referralCategory === 'PRP' || formData.referralCategory === 'Both';

    // Signature validation (typed or drawn)
    const mode = sigs.signatureMode || 'typed';
    if (mode === 'typed') {
      if (!sigs.typedSignature || sigs.typedSignature.trim() === '') {
        errors.signature = 'Signature is required';
      }
    } else {
      if (!sigs.signatureDataUrl) {
        errors.signature = 'Please draw your signature';
      }
    }

    if (!sigs.providerDate) {
      errors.providerDate = 'Date is required';
    }
    if (!sigs.authorizationAgreed) {
      errors.authorizationAgreed = 'You must agree to the authorization statement';
    }

    // Supervision validation (now on signature page)
    if (isPRP && prpData.clinicianUnderSupervision === 'Yes') {
      if (!prpData.supervisorCredentials || prpData.supervisorCredentials.trim() === '') {
        errors.supervisorCredentials = 'Supervisor Credentials are required';
      }
      if (!prpData.supervisorNPI || prpData.supervisorNPI.trim() === '') {
        errors.supervisorNPI = 'Supervisor NPI Number is required';
      }
    }

    if (Object.keys(errors).length > 0) {
      errors.signatures = true;
    }

    return errors;
  };

  const validateCurrentPage = () => {
    const pageComponent = pages[effectivePage]?.component;

    if (pageComponent === ReferralCoverPage) {
      return validateCoverPage();
    }
    if (pageComponent === OMHCReferralForm) {
      return validateOMHCPage();
    }
    if (pageComponent === PRPReferralForm) {
      return validatePRPPage();
    }
    if (pageComponent === SignatureAuthPage) {
      return validateSignaturePage();
    }

    return {};
  };

  const handleNext = () => {
    const errors = validateCurrentPage();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors({});
    if (currentPage < pages.length - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevious = () => {
    setValidationErrors({});
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  useEffect(() => {
    if (currentPage >= pages.length) {
      setCurrentPage(0);
    }
  }, [formData.referralCategory, currentPage, pages.length]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    downloadReferralPDF(formData);
  };

  const handleSubmit = async () => {
    // Validate signature page before submit
    const sigErrors = validateSignaturePage();
    if (Object.keys(sigErrors).length > 0) {
      setValidationErrors(sigErrors);
      return;
    }

    if (submitState === 'submitting' || submitState === 'success') return;
    setSubmitState('submitting');
    setSubmitMessage('');

    try {
      const res = await fetch(`${API_URL}/api/referrals/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ referral_data: formData })
      });

      if (!res.ok) throw new Error(`Server error: ${res.status}`);

      const result = await res.json();
      setSubmitState('success');
      clearDraft();
      setSubmitMessage(
        result.email_sent
          ? 'Referral submitted and email notification sent to the intake team.'
          : 'Referral submitted successfully. Email notification could not be sent — the intake team has been notified via the system.'
      );
    } catch (err) {
      setSubmitState('error');
      setSubmitMessage(`Submission failed: ${err.message}. Please try again.`);
    }
  };

  const effectivePage = Math.min(currentPage, pages.length - 1);
  const CurrentPageComponent = pages[effectivePage].component;

  const modalContent = (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[200] p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl my-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 rounded-t-lg flex items-center justify-between print:hidden">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-bold">OMHC & PRP Referral Form</h2>
              <p className="text-sm text-blue-100">Adult and Minor Referral Packet</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="p-2 hover:bg-blue-800 rounded-lg transition-colors"
              title="Print"
              data-testid="referral-form-print-btn"
            >
              <Printer className="w-5 h-5" />
            </button>
            <button
              onClick={handleDownloadPDF}
              className="p-2 hover:bg-blue-800 rounded-lg transition-colors"
              title="Download PDF"
              data-testid="referral-form-download-btn"
            >
              <Download className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-blue-800 rounded-lg transition-colors"
              title="Close"
              data-testid="referral-form-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="bg-gray-100 px-6 py-3 print:hidden">
          <div className="flex items-center justify-between mb-2">
            {pages.map((page, index) => (
              <div
                key={page.title}
                className={`flex-1 text-center text-sm font-medium ${
                  index === effectivePage
                    ? 'text-blue-600'
                    : index < effectivePage
                    ? 'text-green-600'
                    : 'text-gray-400'
                }`}
              >
                {page.title}
              </div>
            ))}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((effectivePage + 1) / pages.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Form Content */}
        <div className="p-6 max-h-[600px] overflow-y-auto print:max-h-none">
          {/* Draft restored banner */}
          {draftRestored && submitState !== 'success' && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between" data-testid="draft-restored-banner">
              <p className="text-sm text-blue-700">Your previous draft has been restored.</p>
              <button
                type="button"
                onClick={() => {
                  clearDraft();
                  setDraftRestored(false);
                  setCurrentPage(0);
                  setFormData({
                    referralDate: new Date().toISOString().split('T')[0],
                    referralSource: '', referringProvider: '', credentials: '', npi: '',
                    phone: '', fax: '', email: '', contactMethod: 'Phone',
                    urgencyLevel: 'Routine', clientType: 'Adult', referralCategory: '',
                    preferredTimeframe: 'Within 14 days', referralStatus: 'New Referral',
                    omhc: {}, prp: {}, signatures: {}, attachments: []
                  });
                }}
                className="text-xs font-semibold text-blue-600 hover:text-blue-800 underline ml-4 whitespace-nowrap"
                data-testid="start-fresh-btn"
              >
                Start Fresh
              </button>
            </div>
          )}

          {submitState === 'success' ? (
            <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="referral-submit-success">
              <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Referral Submitted</h3>
              <p className="text-gray-600 max-w-md">{submitMessage}</p>
              <div className="mt-8 flex gap-4">
                <button
                  onClick={() => downloadReferralPDF(formData)}
                  className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
                  data-testid="download-pdf-btn"
                >
                  <Download className="w-5 h-5" />
                  Download PDF
                </button>
                <button
                  onClick={onClose}
                  className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                  data-testid="referral-close-after-submit"
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            <CurrentPageComponent
              formData={formData}
              onDataUpdate={handleDataUpdate}
              validationErrors={validationErrors}
            />
          )}
        </div>

        {/* Submission error banner */}
        {submitState === 'error' && (
          <div className="mx-6 mb-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2" data-testid="referral-submit-error">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{submitMessage}</p>
          </div>
        )}

        {/* Navigation Footer */}
        {submitState !== 'success' && (
        <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex items-center justify-between border-t print:hidden">
          <button
            onClick={handlePrevious}
            disabled={effectivePage === 0 || submitState === 'submitting'}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="referral-form-prev-btn"
          >
            Previous
          </button>
          <div className="text-sm text-gray-600">
            Page {effectivePage + 1} of {pages.length}
          </div>
          {effectivePage === pages.length - 1 ? (
            <button
              onClick={handleSubmit}
              disabled={submitState === 'submitting'}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2 font-semibold"
              data-testid="referral-submit-btn"
            >
              {submitState === 'submitting' ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Referral'
              )}
            </button>
          ) : (
            <button
              onClick={handleNext}
              className="px-6 py-2 rounded-lg transition-colors bg-blue-600 text-white hover:bg-blue-700"
              data-testid="referral-form-next-btn"
            >
              Next
            </button>
          )}
        </div>
        )}

        {/* Print Footer */}
        <div className="hidden print:block text-center text-xs text-gray-500 py-2 border-t">
          <p>OMHC & PRP Referral Form | Version 1.0 | {new Date().toLocaleDateString()} | Page {effectivePage + 1} of {pages.length}</p>
          <p>SDORA WELLBEING | Confidential Healthcare Document</p>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default ReferralFormModal;
